import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";

function when(value) {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

export default function EarnKaroCatalog() {
  const qc = useQueryClient();
  const [error, setError] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["admin-earnkaro-catalog"],
    queryFn: async () => (await api.get("/admin/earnkaro/catalog")).data,
  });

  const deactivate = async (key) => {
    setError("");
    try {
      await api.post("/admin/earnkaro/catalog/deactivate", { key });
      await qc.invalidateQueries({ queryKey: ["admin-earnkaro-catalog"] });
    } catch (err) {
      setError(apiError(err));
    }
  };

  const stores = data?.stores || [];
  const categories = data?.categories || [];
  const products = data?.products || [];
  const earnkaroDays = data?.stale_days?.earnkaro ?? 3;
  const inrdealsDays = data?.stale_days?.inrdeals ?? 14;

  return (
    <section className="mb-10 border border-aura-border rounded-2xl p-5 bg-aura-surface" data-testid="earnkaro-imported">
      <div className="font-serif text-xl tracking-tight">Imported catalog</div>
      <p className="text-xs text-aura-muted mt-1" data-testid="earnkaro-imported-stale">
        Products posted by the EarnKaro browser job. An EarnKaro short link stays visible for {earnkaroDays} days after it was last imported. An inr.deals link stays visible for {inrdealsDays} days.
      </p>
      <div className="grid sm:grid-cols-3 gap-3 my-4 text-sm">
        <div className="border border-aura-border p-3">
          <div className="text-[11px] uppercase tracking-luxe text-aura-muted">Active</div>
          <div className="font-serif text-2xl" data-testid="earnkaro-imported-active">{isLoading ? "—" : data?.active ?? 0}</div>
        </div>
        <div className="border border-aura-border p-3">
          <div className="text-[11px] uppercase tracking-luxe text-aura-muted">Hidden</div>
          <div className="font-serif text-2xl">{isLoading ? "—" : data?.inactive ?? 0}</div>
        </div>
        <div className="border border-aura-border p-3">
          <div className="text-[11px] uppercase tracking-luxe text-aura-muted">Last import</div>
          <div className="text-sm mt-2" data-testid="earnkaro-imported-when">{when(data?.last_import_at)}</div>
        </div>
      </div>
      {error ? <p className="text-sm text-aura-accent mb-3">{error}</p> : null}
      <div className="flex flex-wrap gap-4 mb-4 text-xs text-aura-muted">
        <div data-testid="earnkaro-imported-stores">
          {stores.length ? stores.map((row) => `${row.store} ${row.count}`).join(" · ") : "No stores yet"}
        </div>
        <div data-testid="earnkaro-imported-categories">
          {categories.length ? categories.map((row) => `${row.category} ${row.count}`).join(" · ") : ""}
        </div>
      </div>
      <div className="border border-aura-border divide-y divide-aura-border">
        {products.length === 0 ? (
          <div className="px-4 py-4 text-sm text-aura-muted">No imported products yet.</div>
        ) : products.map((row) => (
          <div key={row.key} className="px-4 py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`earnkaro-imported-${row.go_id || row.key}`}>
            <div className="min-w-0">
              <div className="text-sm font-medium">{row.title}</div>
              <div className="text-xs text-aura-muted">{row.store} · {row.category} · ₹{row.price_inr} · {row.active ? "showing" : "hidden"}</div>
            </div>
            {row.active ? (
              <button
                type="button"
                onClick={() => deactivate(row.key)}
                className="text-xs border border-aura-border rounded-full px-3 py-1"
                data-testid={`earnkaro-deactivate-${row.go_id || "row"}`}
              >
                Deactivate
              </button>
            ) : null}
          </div>
        ))}
      </div>
    </section>
  );
}
